import { Box, HStack, Stack, VStack } from "@chakra-ui/layout";
import { useEffect, useState } from "react";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import LoadingIndicator from "../components/Loading";
import {
	useCustomerAccount,
	useCustomerAccountData,
	useLoggedInUser,
	useUpdateUserPassword,
} from "@api/hooks";
import {
	Text,
	Divider,
	Button,
	Input,
	useDisclosure,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useToast,
} from "@chakra-ui/react";
import SimpleInput from "../components/ChakraUI/SimpleInput";

const phoneRegex = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const passwordRegex = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/;

export enum PasswordValidity {
	PASSWORDS_NOT_MATCHED = "Passwords not matched",
	NOT_FOLLOWED_THE_CONFIGURED_VALIDATION = "Not followed the configured validation",
	NO_ERROR = "No error",
}

const MyProfilePage = () => {
	const toast = useToast();
	const user = useLoggedInUser();
	const { customerAccountUser, isLoading, refetch } = useCustomerAccountData(user?.id);
	const {
		isOpen: isOpenEditPasswordModal,
		onOpen: onOpenEditPasswordModal,
		onClose: onCloseEditPasswordModal,
	} = useDisclosure();

	const {
		isOpen: isOpenEditEmailModal,
		onOpen: onOpenEditEmailModal,
		onClose: onCloseEditEmailModal,
	} = useDisclosure();
	const {
		mutate: updateCustomerAccount,
		isLoading: isLoadingUpdate,
		isError: isErrorUpdateAccount,
	} = useCustomerAccount(
		() => {
			refetch();
			isOpenEditEmailModal && onCloseEditEmailModal();
			toast({
				title: "Profildata uppdaterad",
				status: "success",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
		() => {
			toast({
				title: "Uppdateringen av profildata misslyckades",
				status: "error",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
	);
	const {
		mutate: updatePassword,
		isLoading: isLoadingPasswordUpdate,
		isError: isErrorPasswordUpdate,
	} = useUpdateUserPassword(
		() => {
			isOpenEditPasswordModal && onCloseEditPasswordModal();
			toast({
				title: "Lösenordet har uppdaterats",
				status: "success",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
		() => {
			toast({
				title: "Uppdateringen av lösenordet misslyckades",
				status: "error",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
	);
	const [title, setTitle] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [hasPhoneNumberError, setHasPhoneNumberError] = useState(false);
	const [passwordError, setPasswordError] = useState<PasswordValidity>(PasswordValidity.NO_ERROR);
	const [hasEmailError, setHasEmailError] = useState(false);

	useEffect(() => {
		if (customerAccountUser?.profile) {
			const profile = customerAccountUser.profile;
			setTitle(profile.title ?? "");
			setPhoneNumber(profile.phoneNumber ?? "");
			setEmail(customerAccountUser.email ?? "");
		}
	}, [customerAccountUser]);

	const disableUpdateAccount =
		customerAccountUser?.profile?.title !== title || customerAccountUser?.profile?.phoneNumber !== phoneNumber.trim()
			? false
			: true;

	const disableEmailUpdate = customerAccountUser?.email && customerAccountUser.email !== email.trim() ? false : true;
	const disablePasswordUpdate = !password || !confirmPassword || !newPassword ? true : false;

	const handleUpdateAccount = async () => {
		if (phoneRegex.test(phoneNumber)) {
			try {
				await updateCustomerAccount({
					userId: user.id,
					body: { profile: { phoneNumber, title } },
				});
			} catch (error) {
				toast({
					title: "Försök igen senare",
					status: "error",
					description: JSON.stringify(error),
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
		} else {
			setHasPhoneNumberError(true);
		}
	};

	const handleUpdateEmail = async () => {
		if (emailRegex.test(email)) {
			try {
				await updateCustomerAccount({
					userId: user.id,
					body: { email, password },
				});
			} catch (error) {
				toast({
					title: "Försök igen senare",
					status: "error",
					description: JSON.stringify(error),
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
		} else {
			setHasEmailError(true);
		}
	};

	const handlePasswordUpdate = async () => {
		if (confirmPassword !== newPassword) {
			setPasswordError(PasswordValidity.PASSWORDS_NOT_MATCHED);
			return;
		}
		if (!(passwordRegex.test(confirmPassword) && passwordRegex.test(newPassword))) {
			setPasswordError(PasswordValidity.NOT_FOLLOWED_THE_CONFIGURED_VALIDATION);
			return;
		} else {
			try {
				await updatePassword({
					currentPassword: password,
					newPassword: confirmPassword,
				});
			} catch (error) {
				toast({
					title: "Försök igen senare",
					status: "error",
					description: JSON.stringify(error),
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
		}
	};

	return (
		<LoggedInLayout width="l" bg={"#edeff3"}>
			<VStack spacing={"0.5rem"} align={"flex-start"} w="full">
				<Text fontWeight={600} fontSize={24} mt={2} mb={0}>
					Min profil
				</Text>
				{!isLoading && (
					<Text fontWeight={500} fontSize={16} m={0}>
						{user?.firstName + " " + user?.lastName}
					</Text>
				)}
				{isLoading && <LoadingIndicator size={"s"} />}
				{!isLoading && (
					<Box borderRadius={"lg"} backgroundColor={"white"} p={8} my={3} w="full">
						<VStack spacing="5" alignItems={"start"} w="full">
							<VStack alignItems="start" w="full" spacing="1">
								<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
									Allmänt
								</Text>
								<Divider mt={2} />
							</VStack>
							<Stack
								direction={{ base: "column", md: "row" }}
								alignItems={{ base: "stretch", md: "flex-start" }}
								w="full"
								spacing="6"
								justify={{ base: "flex-start", md: "center" }}
							>
								<SimpleInput width={300} label="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />
								<VStack alignItems={"start"} w={{ base: "100%", md: "auto" }}>
									<SimpleInput
										width={300}
										label="Telefon"
										value={phoneNumber}
										onChange={(e) => {
											setPhoneNumber(e.target.value);
											setHasPhoneNumberError(false);
										}}
										isInvalid={hasPhoneNumberError}
									/>
									{hasPhoneNumberError && (
										<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m={0}>
											Ogiltigt telefonnummer
										</Text>
									)}
								</VStack>

								<Button
									variant="solid"
									height="40px"
									borderRadius={"20px"}
									width={{ base: "100%", md: "200px" }}
									color="white"
									bg={"#2A2A2A"}
									_hover={{
										backgroundColor: disableUpdateAccount ? "#9fa0a3" : "#2A2A2A",
									}}
									onClick={handleUpdateAccount}
									isDisabled={disableUpdateAccount}
									isLoading={isLoadingUpdate}
									mt={{ base: 2, md: 7 }}
									ml={{ base: 0, md: "auto" }}
								>
									Spara ändringar
								</Button>
							</Stack>
							<VStack alignItems="start" w="full" spacing="1">
								<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
									Inlogg
								</Text>
								<Divider mt={2} />
							</VStack>
							<HStack bg="#EDEFF3" w="100%" p={6} justify={"space-between"} borderRadius={"2px"}>
								<VStack alignItems={"start"} flex="1" minW={0}>
									<Text fontWeight={600} fontSize={14} color={"#2A2A2A"} m="0">
										Ändra lösenord
									</Text>
									<Input
										variant="unstyled"
										color={"#2A2A2A"}
										fontSize={14}
										fontWeight={600}
										readOnly
										type="password"
										value={"random_password"}
										w="full"
									/>
								</VStack>
								<Button
									onClick={() => {
										onOpenEditPasswordModal();
										setPassword("");
										setConfirmPassword("");
										setNewPassword("");
										setPasswordError(PasswordValidity.NO_ERROR);
									}}
									size="xs"
									color={"#219CD0"}
									variant="link"
								>
									Ändra
								</Button>
							</HStack>
							<HStack bg="#EDEFF3" w="100%" p={6} justify={"space-between"} borderRadius={"2px"}>
								<VStack alignItems={"start"} flex="1" minW={0}>
									<Text fontWeight={600} fontSize={14} color={"#2A2A2A"} m="0">
										E-post
									</Text>
									<Input
										variant="unstyled"
										color={"#2A2A2A94"}
										fontSize={16}
										fontWeight={400}
										readOnly
										type="email"
										value={customerAccountUser?.email ?? ""}
										w="full"
									/>
								</VStack>
								<Button
									onClick={() => {
										onOpenEditEmailModal();
										setPassword("");
										setHasEmailError(false);
									}}
									size="xs"
									color={"#219CD0"}
									variant="link"
								>
									Ändra
								</Button>
							</HStack>
						</VStack>
					</Box>
				)}
			</VStack>

			{/* Edit password Modal */}

			<Modal isOpen={isOpenEditPasswordModal} onClose={onCloseEditPasswordModal}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader alignSelf={"center"}>Ändra lösenord</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<VStack justify={"flex-start"} gap={5}>
							<SimpleInput
								width={300}
								type="password"
								label="Nuvarande lösenord"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
							{isErrorPasswordUpdate && (
								<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m={0}>
									Lösenordet du angav är felaktigt
								</Text>
							)}
							<SimpleInput
								width={300}
								type="password"
								label="Nytt lösenord"
								value={newPassword}
								onChange={(e) => {
									setNewPassword(e.target.value);
									setPasswordError(PasswordValidity.NO_ERROR);
								}}
								isInvalid={passwordError !== PasswordValidity.NO_ERROR}
							/>
							<SimpleInput
								width={300}
								type="password"
								value={confirmPassword}
								onChange={(e) => {
									setConfirmPassword(e.target.value);
									setPasswordError(PasswordValidity.NO_ERROR);
								}}
								isInvalid={passwordError !== PasswordValidity.NO_ERROR}
							/>
							{passwordError === PasswordValidity.PASSWORDS_NOT_MATCHED && (
								<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m={0}>
									Inmatningen i de två fälten matchar inte varandra.
								</Text>
							)}
							<Text
								fontWeight={500}
								width={300}
								color={passwordError === PasswordValidity.NOT_FOLLOWED_THE_CONFIGURED_VALIDATION ? "#EB5757" : ""}
								fontSize={"12px"}
								m={0}
							>
								Lösenordet behöver innehålla 8 tecken med minst en stor och en liten bokstav samt ett nummer.
							</Text>
						</VStack>
					</ModalBody>

					<ModalFooter>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="accent.500"
								color={"#FFFFFF"}
								width={300}
								mx={3}
								onClick={handlePasswordUpdate}
								isDisabled={disablePasswordUpdate}
								isLoading={isLoadingPasswordUpdate}
							>
								Spara ändringar
							</Button>
							<Button borderRadius={"2px"} width={300} mx={3} onClick={onCloseEditPasswordModal}>
								Avbryt
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Edit email Modal */}

			<Modal isOpen={isOpenEditEmailModal} onClose={onCloseEditEmailModal}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader alignSelf={"center"}>Ändra e-post</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<VStack justify={"flex-start"} gap={5}>
							<SimpleInput
								width={300}
								type="email"
								label="E-post"
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									setHasEmailError(false);
								}}
								isInvalid={hasEmailError}
							/>
							{hasEmailError && (
								<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m={0}>
									Ogiltig e-post
								</Text>
							)}
							<SimpleInput
								width={300}
								type="password"
								label="Bekräfta genom att ange lösenord"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
							{isErrorUpdateAccount && (
								<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m={0}>
									Lösenordet du angav är felaktigt
								</Text>
							)}
						</VStack>
					</ModalBody>

					<ModalFooter mb={5}>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="accent.500"
								color={"#FFFFFF"}
								width={300}
								mx={3}
								onClick={handleUpdateEmail}
								isLoading={isLoadingUpdate}
								isDisabled={disableEmailUpdate}
							>
								Spara ändringar
							</Button>
							<Button borderRadius={"2px"} width={300} mx={3} onClick={onCloseEditEmailModal}>
								Avbryt
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</LoggedInLayout>
	);
};

export default MyProfilePage;
